import { useEffect, useRef, useState } from 'react';
import { createFileRoute, useRouter } from '@tanstack/react-router';
import {
  SEARCH_DEBOUNCE_MS,
  SEARCH_MIN_CHARS,
  YT_ID_RE,
  classifyKaraokeVocals,
  strings,
  ytThumbnailUrl,
  type Degraded,
  type PlaybackMode,
  type SongSearchResultDTO,
  type YtCandidateDTO,
} from '@singparty/shared';
import { songApi, queueApi, adminApi } from '@/lib/songApi';
import { ApiClientError } from '@/lib/apiClient';
import { useMe, useQueue } from '@/stores/sessionStore';
import { useLocalMedia } from '@/hooks/useServerConfig';
import { Button } from '@/components/ui/Button';

export const Route = createFileRoute('/s/$sid/add')({
  component: AddScreen,
});

type Tab = 'search' | 'party' | 'popular' | 'artist' | 'downloaded';

function AddScreen() {
  const { sid } = Route.useParams();
  const router = useRouter();
  const goBack = () => router.history.back();
  // Popular is the landing tab so an empty search box still shows something to
  // tap; typing flips to Search (see the input's onChange).
  const [tab, setTab] = useState<Tab>('popular');
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<SongSearchResultDTO[]>([]);
  const [loading, setLoading] = useState(false);
  const [degraded, setDegraded] = useState<Degraded | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [selected, setSelected] = useState<SongSearchResultDTO | null>(null);
  const abortRef = useRef<AbortController | null>(null);
  const localMedia = useLocalMedia();
  const me = useMe();
  const isHost = me?.role === 'host';

  const tabs: Tab[] = localMedia
    ? ['popular', 'search', 'downloaded', 'party', 'artist']
    : ['popular', 'search', 'party', 'artist'];

  /** Host-only: delete a downloaded file from the Downloaded tab. */
  async function deleteDownload(r: SongSearchResultDTO) {
    setResults((rs) => rs.filter((x) => x.id !== r.id)); // optimistic
    try {
      if (r.ytVideoId) await adminApi.deleteDownload(r.ytVideoId);
    } catch {
      setError('Could not delete download');
    }
  }

  // Debounced search (350ms, cancel stale) — 05 §2.6 / 03 §2.1.
  useEffect(() => {
    if (tab !== 'search') return;
    if (query.trim().length < SEARCH_MIN_CHARS) {
      setResults([]);
      setDegraded(null);
      return;
    }
    const handle = setTimeout(async () => {
      abortRef.current?.abort();
      const ctrl = new AbortController();
      abortRef.current = ctrl;
      setLoading(true);
      setError(null);
      try {
        const res = await songApi.search(sid, query.trim(), ctrl.signal);
        setResults(res.results);
        setDegraded(res.degraded ?? null);
      } catch (err) {
        if (err instanceof DOMException && err.name === 'AbortError') return;
        setError(strings.search.retry);
      } finally {
        setLoading(false);
      }
    }, SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(handle);
  }, [query, sid, tab]);

  // Browse tabs.
  useEffect(() => {
    if (tab === 'search') return;
    const by = tab === 'party' ? 'session' : tab === 'popular' ? 'popular' : tab === 'downloaded' ? 'downloaded' : 'artist';
    setLoading(true);
    setError(null);
    songApi
      .browse(sid, by)
      .then((res) => setResults(res.results))
      .catch(() => setError('Could not load'))
      .finally(() => setLoading(false));
  }, [tab, sid]);

  return (
    <div className="mx-auto flex min-h-full max-w-[560px] flex-col px-4 py-4">
      <header className="mb-3 flex items-center gap-2">
        <button aria-label="Back" onClick={goBack} className="rounded-full px-2 py-1 text-lg">
          ←
        </button>
        {/* No autoFocus: the phone keyboard would cover the Popular list the
            screen now opens on. */}
        <input
          value={query}
          onChange={(e) => {
            const next = e.target.value;
            setQuery(next);
            // Typing jumps to Search; clearing the box lands back on Popular
            // rather than on an empty Search tab.
            setTab(next.trim() ? 'search' : 'popular');
          }}
          placeholder={strings.search.placeholder}
          className="h-11 flex-1 rounded-pill border border-border bg-surface px-4 outline-none focus-visible:border-primary"
        />
      </header>

      <div className="mb-3 flex gap-2 overflow-x-auto">
        {tabs.map((t) => (
          <button
            key={t}
            onClick={() => setTab(t)}
            className={`whitespace-nowrap rounded-pill px-3 py-1 text-sm ${
              tab === t ? 'bg-primary text-white' : 'bg-surface-2'
            }`}
          >
            {t === 'search'
              ? strings.search.tabSearch
              : t === 'downloaded'
                ? '⬇ Downloaded'
                : t === 'party'
                  ? strings.search.browseThisParty
                  : t === 'popular'
                    ? strings.search.browsePopular
                    : strings.search.browseByArtist}
          </button>
        ))}
      </div>

      {degraded === 'metadata' && (
        <div className="mb-3 rounded-card bg-amber-100 px-3 py-2 text-sm text-amber-800">
          {strings.search.degradedMetadata}
        </div>
      )}

      <div className="flex-1 space-y-2">
        {loading && [0, 1, 2].map((i) => <div key={i} className="h-16 animate-pulse rounded-card bg-surface" />)}
        {!loading && results.length === 0 && tab === 'search' && query.trim().length >= SEARCH_MIN_CHARS && (
          <div className="py-10 text-center text-text-secondary">
            <p>{strings.search.noMatches}</p>
          </div>
        )}
        {!loading && results.length === 0 && tab === 'downloaded' && (
          <div className="py-10 text-center text-text-secondary">
            <p>No downloaded songs yet.</p>
            <p className="mt-1 text-sm">Songs you add are downloaded and appear here for instant replay.</p>
          </div>
        )}
        {!loading &&
          results.map((r) => (
            <ResultRow
              key={r.id}
              result={r}
              onTap={() => setSelected(r)}
              onDelete={tab === 'downloaded' && isHost ? () => deleteDownload(r) : undefined}
            />
          ))}
        {error && (
          <div className="py-6 text-center">
            <p className="mb-2 text-danger">{error}</p>
            <Button variant="secondary" size="sm" onClick={() => setQuery((q) => q + ' ')}>
              {strings.search.retry}
            </Button>
          </div>
        )}
      </div>

      {selected && (
        <ConfirmSheet sid={sid} result={selected} onClose={() => setSelected(null)} onAdded={goBack} />
      )}
    </div>
  );
}

function ResultRow({
  result,
  onTap,
  onDelete,
}: {
  result: SongSearchResultDTO;
  onTap: () => void;
  onDelete?: () => void;
}) {
  const queue = useQueue();
  const inQueue = queue.some((q) => q.status === 'queued' && q.song.id === result.id);
  return (
    <div className="flex w-full items-center gap-1 rounded-card border border-border bg-surface">
      <button onClick={onTap} className="flex min-w-0 flex-1 items-center gap-3 p-2 text-left">
        {result.artworkUrl ? (
          <img src={result.artworkUrl} alt="" className="h-14 w-14 rounded-lg object-cover" />
        ) : (
          <div className="flex h-14 w-14 items-center justify-center rounded-lg bg-surface-2 text-lg">🎵</div>
        )}
        <div className="min-w-0 flex-1">
          <div className="truncate font-semibold">{result.title}</div>
          <div className="truncate text-sm text-text-secondary">
            {result.artist}
            {result.durationS ? ` · ${fmtDuration(result.durationS)}` : ''}
          </div>
          <div className="mt-1 flex gap-1">
            {onDelete && <span className="rounded bg-accent/20 px-1 text-[10px] text-accent">⬇ downloaded</span>}
            {result.explicit && <span className="rounded bg-surface-2 px-1 text-[10px]">E</span>}
            {inQueue && <span className="rounded bg-primary/20 px-1 text-[10px] text-primary">{strings.search.inQueueChip}</span>}
          </div>
        </div>
      </button>
      {onDelete && (
        <button
          aria-label="Delete download"
          title="Delete downloaded file"
          onClick={(e) => {
            e.stopPropagation();
            onDelete();
          }}
          className="mr-1 shrink-0 rounded-full px-3 py-2 text-danger hover:bg-danger/10"
        >
          🗑
        </button>
      )}
    </div>
  );
}

function ConfirmSheet({
  sid,
  result,
  onClose,
  onAdded,
}: {
  sid: string;
  result: SongSearchResultDTO;
  onClose: () => void;
  onAdded: () => void;
}) {
  const queue = useQueue();
  const [candidates, setCandidates] = useState<YtCandidateDTO[]>([]);
  const [chosen, setChosen] = useState<YtCandidateDTO | null>(null);
  const [resolvedPrimary, setResolvedPrimary] = useState<string | undefined>(undefined);
  const [degraded, setDegraded] = useState<Degraded | null>(null);
  const [resolving, setResolving] = useState(true);
  const [singer, setSinger] = useState('');
  const [mode, setMode] = useState<PlaybackMode>('video');
  const [pasteId, setPasteId] = useState('');
  const [pasteError, setPasteError] = useState<string | null>(null);
  const [adding, setAdding] = useState(false);
  const [addError, setAddError] = useState<string | null>(null);

  const dup = queue.find((q) => q.status === 'queued' && q.song.id === result.id);

  useEffect(() => {
    let cancelled = false;
    songApi
      .resolve(sid, { songId: result.id, title: result.title, artist: result.artist, durationS: result.durationS })
      .then((res) => {
        if (cancelled) return;
        setCandidates(res.candidates);
        setChosen(res.candidates[0] ?? null);
        setResolvedPrimary(res.song.ytVideoId);
        setDegraded(res.degraded ?? null);
        // No karaoke video → default to lyrics_only (03 §2.3 fallback).
        if (res.candidates.length === 0) setMode('lyrics_only');
      })
      .finally(() => !cancelled && setResolving(false));
    return () => {
      cancelled = true;
    };
  }, [sid, result]);

  function extractYtId(raw: string): string | null {
    const trimmed = raw.trim();
    if (YT_ID_RE.test(trimmed)) return trimmed;
    const m = /(?:v=|youtu\.be\/|embed\/)([A-Za-z0-9_-]{11})/.exec(trimmed);
    return m ? m[1]! : null;
  }

  async function usePasted() {
    setPasteError(null);
    const id = extractYtId(pasteId);
    if (!id) return setPasteError('Paste a valid YouTube link or ID');
    try {
      const v = await songApi.validateYt(sid, id);
      if (!v.ok) return setPasteError(strings.confirm.badEmbed);
      const c: YtCandidateDTO = { videoId: id, title: v.title ?? 'Pasted video', channel: v.channel ?? '', durationS: v.durationS ?? null };
      setCandidates((cs) => [c, ...cs]);
      setChosen(c);
      setMode('video');
    } catch {
      setPasteError(strings.confirm.badEmbed);
    }
  }

  async function add() {
    setAdding(true);
    setAddError(null);
    const clientOpId = crypto.randomUUID();
    try {
      // Attach the chosen video (pasted or alternate) to THIS item at creation
      // whenever it differs from the song's resolved primary — including when
      // there is no resolved primary at all (no API key → paste-link path). Sent
      // in the add request (not a follow-up changeMode) so the right video is the
      // one downloaded, and so it works while the item is still 'pending'. (03 §2.3)
      const override = chosen && chosen.videoId !== resolvedPrimary ? chosen.videoId : undefined;
      await queueApi.add(sid, {
        clientOpId,
        song: { songId: result.id },
        singerLabel: singer.trim() || undefined,
        mode,
        ...(override ? { ytVideoId: override } : {}),
      });
      onAdded();
    } catch (err) {
      if (err instanceof ApiClientError) {
        if (err.code === 'LIMIT_REACHED') setAddError(strings.errors.LIMIT_REACHED);
        else if (err.code === 'RATE_LIMITED') setAddError(strings.errors.RATE_LIMITED);
        else setAddError(err.message);
      } else setAddError(strings.errors.generic);
      setAdding(false);
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-end justify-center bg-black/60" onClick={onClose}>
      <div className="max-h-[90vh] w-full max-w-md overflow-y-auto rounded-t-card border border-border bg-surface p-5" onClick={(e) => e.stopPropagation()}>
        <div className="mb-4 flex items-center gap-3">
          {result.artworkUrl && <img src={result.artworkUrl} alt="" className="h-16 w-16 rounded-lg object-cover" />}
          <div>
            <div className="font-bold">{result.title}</div>
            <div className="text-sm text-text-secondary">{result.artist}</div>
          </div>
        </div>

        {/* Video candidate — picked from a scrollable strip of thumbnails, so
            the cover art (and the guide-vocals chip) carries the choice rather
            than a list of channel names. */}
        <div className="mb-4 rounded-card bg-bg p-3">
          {resolving ? (
            <div className="flex gap-2 overflow-hidden">
              {[0, 1, 2].map((i) => (
                <div key={i} className="h-33 w-40 shrink-0 animate-pulse rounded-lg bg-surface-2" />
              ))}
            </div>
          ) : candidates.length > 0 ? (
            <>
              <div className="mb-2 flex items-baseline justify-between gap-2">
                <span className="text-xs text-text-secondary">{strings.confirm.pickVideo}</span>
                {candidates.length > 1 && (
                  <span className="shrink-0 text-[10px] text-text-secondary">{candidates.length} versions</span>
                )}
              </div>
              <div className="-mx-1 flex snap-x snap-mandatory gap-2 overflow-x-auto px-1 pb-1">
                {candidates.map((c) => (
                  <CandidateCard
                    key={c.videoId}
                    candidate={c}
                    selected={chosen?.videoId === c.videoId}
                    onSelect={() => {
                      setChosen(c);
                      setMode('video');
                    }}
                  />
                ))}
              </div>
            </>
          ) : (
            <p className="text-sm text-text-secondary">No karaoke video found — paste one below.</p>
          )}
          {degraded === 'quota' && <p className="mt-2 text-xs text-amber-800">{strings.errors.QUOTA_DEGRADED}</p>}

          <div className="mt-3 flex gap-2">
            <input
              value={pasteId}
              onChange={(e) => setPasteId(e.target.value)}
              placeholder={strings.confirm.pasteLinkRow}
              className="h-9 flex-1 rounded-pill border border-border bg-surface px-3 text-sm outline-none"
            />
            <Button size="sm" variant="secondary" onClick={usePasted}>
              Use
            </Button>
          </div>
          {pasteError && <p className="mt-1 text-xs text-danger">{pasteError}</p>}
        </div>

        {/* Sing as */}
        <label className="mb-1 block text-sm text-text-secondary">{strings.confirm.singAs}</label>
        <input
          value={singer}
          onChange={(e) => setSinger(e.target.value)}
          placeholder="Your name"
          className="mb-4 h-11 w-full rounded-pill border border-border bg-bg px-4 outline-none"
        />

        {dup && (
          <div className="mb-3 rounded-card bg-amber-100 px-3 py-2 text-sm text-amber-800">
            {strings.confirm.dupWarn(dup.requestedBy.displayName)}
          </div>
        )}
        {addError && <div className="mb-3 text-sm text-danger">{addError}</div>}

        <Button size="lg" className="w-full" loading={adding} disabled={resolving} onClick={add}>
          {strings.confirm.add}
        </Button>
      </div>
    </div>
  );
}

/** One selectable karaoke video: cover frame, title, channel, vocals chip. */
function CandidateCard({
  candidate,
  selected,
  onSelect,
}: {
  candidate: YtCandidateDTO;
  selected: boolean;
  onSelect: () => void;
}) {
  const vocals = classifyKaraokeVocals(candidate.title);
  return (
    <button
      type="button"
      onClick={onSelect}
      aria-pressed={selected}
      className={`w-40 shrink-0 snap-start overflow-hidden rounded-lg border text-left ${
        selected ? 'border-primary bg-primary/10' : 'border-border bg-surface'
      }`}
    >
      <div className="relative aspect-video w-full bg-surface-2">
        <img
          src={ytThumbnailUrl(candidate.videoId)}
          alt=""
          loading="lazy"
          className="h-full w-full object-cover"
          onError={(e) => (e.currentTarget.style.visibility = 'hidden')}
        />
        {candidate.durationS != null && (
          <span className="absolute bottom-1 right-1 rounded bg-black/75 px-1 text-[10px] tabular-nums text-white">
            {fmtDuration(candidate.durationS)}
          </span>
        )}
        {selected && (
          <span className="absolute left-1 top-1 flex h-5 w-5 items-center justify-center rounded-full bg-primary text-[11px] text-white">
            ✓
          </span>
        )}
      </div>
      <div className="p-2">
        <div className="line-clamp-2 text-xs leading-snug font-medium">{candidate.title}</div>
        <div className="mt-0.5 truncate text-[11px] text-text-secondary">{candidate.channel}</div>
        {vocals !== 'unknown' && (
          <span
            className={`mt-1 inline-block rounded px-1 text-[10px] ${
              vocals === 'guide' ? 'bg-accent/20 text-accent' : 'bg-surface-2 text-text-secondary'
            }`}
          >
            {vocals === 'guide' ? strings.confirm.vocalsGuide : strings.confirm.vocalsInstrumental}
          </span>
        )}
      </div>
    </button>
  );
}

function fmtDuration(s: number): string {
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;
}
